import axios from "axios";
import {
  useInfiniteQuery,
  QueryFunctionContext,
  useQueryClient,
} from "@tanstack/react-query";
import { PaginatedReportsDTO } from "pages/reports/bi-dashboard-next/models/PaginatedReportsDTO";
import { ReportCriteriaDTO } from "pages/reports/bi-dashboard-next/models/ReportCriteriaDTO";
import getLocalApiUrl from "pages/reports/utils/getLocalApiUrl";
import { useState } from "react";

interface FetchReportsContext extends QueryFunctionContext {
  queryKey: [string, ReportCriteriaDTO?, string?, string?];
}

const fetchReports = async (
  context: FetchReportsContext,
): Promise<PaginatedReportsDTO> => {
  const pageParam = (context.pageParam as number) ?? 0;
  const [_, criteria, sortBy, sortDirection] = context.queryKey;

  const response = await axios.get(getLocalApiUrl("/reporting/reports"), {
    params: {
      ...criteria,
      page: pageParam,
      size: 100,
      sortBy: sortBy || "name",
      sortDirection: sortDirection || "asc",
    },
    paramsSerializer: {
      indexes: null,
    },
  });

  return response.data;
};

export const useReportsData = (
  criteria?: ReportCriteriaDTO,
  sortBy: string = "name",
  sortDirection: string = "asc",
) => {
  const queryClient = useQueryClient();
  const [isTogglingFavorite, setIsTogglingFavorite] = useState<
    Record<number, boolean>
  >({});

  const {
    isLoading,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    error,
    isError,
  } = useInfiniteQuery<PaginatedReportsDTO>({
    queryKey: ["reports", criteria, sortBy, sortDirection],
    queryFn: fetchReports as any,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages - 1) {
        return undefined;
      }
      return lastPage.page + 1;
    },
    enabled: true,
  });

  const reports =
    data?.pages?.flatMap((page: PaginatedReportsDTO) => page.content) || [];

  const toggleFavorite = async (reportId: number, favorited: boolean) => {
    if (isTogglingFavorite[reportId]) return;

    setIsTogglingFavorite((prev) => ({ ...prev, [reportId]: true }));

    queryClient.setQueryData(
      ["reports", criteria, sortBy, sortDirection],
      (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: PaginatedReportsDTO) => ({
            ...page,
            content: page.content.map((report) => {
              if (report.id === reportId) {
                return { ...report, favorited: !favorited };
              }
              return report;
            }),
          })),
        };
      },
    );

    try {
      if (!favorited) {
        await axios.delete(
          getLocalApiUrl(`/reporting/reports/${reportId}/favorite`),
        );
      } else {
        await axios.post(
          getLocalApiUrl(`/reporting/reports/${reportId}/favorite`),
        );
      }

      await queryClient.invalidateQueries({ queryKey: ["reports"] });
    } catch (error) {
      console.error("Error toggling favorite status:", error);
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    } finally {
      setIsTogglingFavorite((prev) => ({ ...prev, [reportId]: false }));
    }
  };

  return {
    isLoading,
    reports,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    error,
    isError,
    toggleFavorite,
    isTogglingFavorite,
  };
};
